import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useLocale } from '@/hooks/useLocale';
import type { AppLocale } from '@/types/common.types';
import { LOCALE_OPTIONS, LocaleFlag } from './LocaleFlag';

interface LanguageSwitcherProps {
  /**
   * `compact` — icon-only trigger for tight spaces (app bar).
   * `pill` — flag + label + chevron trigger for open backgrounds (auth pages).
   */
  variant?: 'compact' | 'pill';
}

export function LanguageSwitcher({ variant = 'compact' }: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const { locale, changeLocale } = useLocale();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (next: AppLocale) => {
    changeLocale(next);
    handleClose();
  };

  const menuId = `language-menu-${variant}`;

  return (
    <>
      {variant === 'compact' ? (
        <Tooltip title={t('layout.changeLanguage')}>
          <ButtonBase
            onClick={handleOpen}
            aria-label={t('layout.changeLanguage')}
            aria-controls={isOpen ? menuId : undefined}
            aria-haspopup="true"
            aria-expanded={isOpen ? 'true' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '50%',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <LocaleFlag locale={locale} size={22} />
          </ButtonBase>
        </Tooltip>
      ) : (
        <ButtonBase
          onClick={handleOpen}
          aria-label={t('layout.changeLanguage')}
          aria-controls={isOpen ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            pl: 1,
            pr: 0.75,
            py: 0.5,
            borderRadius: 999,
            bgcolor: 'rgba(255, 255, 255, 0.92)',
            boxShadow: 2,
            transition: (theme) => theme.transitions.create(['box-shadow', 'background-color']),
            '&:hover': { bgcolor: 'common.white', boxShadow: 4 },
          }}
        >
          <LocaleFlag locale={locale} size={20} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t(`language.${locale}`)}
          </Typography>
          <KeyboardArrowDownIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
              transform: isOpen ? 'rotate(180deg)' : 'none',
              transition: (theme) => theme.transitions.create('transform'),
            }}
          />
        </ButtonBase>
      )}

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 190 } } }}
      >
        {LOCALE_OPTIONS.map((option) => (
          <MenuItem key={option} selected={option === locale} onClick={() => handleSelect(option)}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Box sx={{ display: 'flex' }}>
                <LocaleFlag locale={option} size={20} />
              </Box>
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">{t(`language.${option}`)}</Typography>
            </ListItemText>
            {option === locale ? <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} /> : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
